import React, { useState, useEffect } from 'react';
import { Upload, Search, FolderOpen, Download, Trash2, FileText, Image, X, AlertCircle, Link2, Copy } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { getPublicFileUrl } from '../../../lib/storage';

interface PatientFile {
  id: string;
  patient_id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  category: string;
  description: string;
  storage_path: string;
  created_at: string;
  patients?: { full_name: string };
}

interface GeneralFile {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  category: string;
  description: string;
  storage_path: string;
  created_at: string;
}

interface Patient {
  id: string;
  full_name: string;
  email: string;
}

const Files = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'patient' | 'general'>('patient');
  const [patientFiles, setPatientFiles] = useState<PatientFile[]>([]);
  const [generalFiles, setGeneralFiles] = useState<GeneralFile[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [selectedPatient, setSelectedPatient] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [category, setCategory] = useState('document');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  const handleCopyUrl = (file: GeneralFile) => {
    const publicUrl = getPublicFileUrl(file.storage_path);
    navigator.clipboard.writeText(publicUrl);
    setCopiedUrl(file.id);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  useEffect(() => {
    fetchPatientFiles();
    fetchGeneralFiles();
    fetchPatients();
  }, []);

  const fetchPatientFiles = async () => {
    try {
      const { data, error } = await supabase
        .from('patient_files')
        .select('*, patients(full_name)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPatientFiles(data || []);
    } catch (error) {
      console.error('Error fetching patient files:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGeneralFiles = async () => {
    try {
      const { data, error } = await supabase
        .from('general_files')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setGeneralFiles(data || []);
    } catch (error) {
      console.error('Error fetching general files:', error);
    }
  };

  const fetchPatients = async () => {
    try {
      const { data, error } = await supabase
        .from('patients')
        .select('id, full_name, email')
        .order('full_name');

      if (error) throw error;
      setPatients(data || []);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setSelectedFiles(filesArray);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (activeTab === 'patient' && !selectedPatient) {
      setError('Please select a patient');
      return;
    }
    if (selectedFiles.length === 0) {
      setError('Please select at least one file');
      return;
    }

    setUploading(true);
    setError('');
    const progress: {[key: string]: number} = {};

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const fileName = file.name;
        progress[fileName] = 0;
        setUploadProgress({...progress});

        const bucket = activeTab === 'patient' ? 'private-files' : 'public-files';
        const filePath = activeTab === 'patient'
          ? `patients/${selectedPatient}/${Date.now()}_${fileName}`
          : `general/${Date.now()}_${fileName}`;

        progress[fileName] = 50;
        setUploadProgress({...progress});

        const { error: uploadError } = await supabase.storage
          .from(bucket)
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        progress[fileName] = 75;
        setUploadProgress({...progress});

        if (activeTab === 'patient') {
          const { error: dbError } = await supabase
            .from('patient_files')
            .insert({
              patient_id: selectedPatient,
              file_name: fileName,
              file_type: file.type,
              file_size: file.size,
              storage_path: filePath,
              category: category,
              description: description,
              uploaded_by: user?.id
            });

          if (dbError) throw dbError;
        } else {
          const { error: dbError } = await supabase
            .from('general_files')
            .insert({
              file_name: fileName,
              file_type: file.type,
              file_size: file.size,
              storage_path: filePath,
              category: category,
              description: description,
              uploaded_by: user?.id
            });

          if (dbError) throw dbError;
        }

        progress[fileName] = 100;
        setUploadProgress({...progress});
      }

      await fetchPatientFiles();
      await fetchGeneralFiles();
      setShowUploadModal(false);
      setSelectedFiles([]);
      setSelectedPatient('');
      setCategory('document');
      setDescription('');
      setUploadProgress({});
    } catch (error: any) {
      console.error('Error uploading files:', error);
      setError(error.message || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (file: PatientFile | GeneralFile) => {
    try {
      const bucket = 'patient_id' in file ? 'private-files' : 'public-files';
      const { data, error } = await supabase.storage
        .from(bucket)
        .download(file.storage_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file');
    }
  };

  const handleDeletePatient = async (file: PatientFile) => {
    if (!confirm(`Are you sure you want to delete ${file.file_name}?`)) {
      return;
    }

    try {
      const { error: storageError } = await supabase.storage
        .from('private-files')
        .remove([file.storage_path]);

      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from('patient_files')
        .delete()
        .eq('id', file.id);

      if (dbError) throw dbError;

      await fetchPatientFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Failed to delete file');
    }
  };

  const handleDeleteGeneral = async (file: GeneralFile) => {
    if (!confirm(`Are you sure you want to delete ${file.file_name}?`)) {
      return;
    }

    try {
      const { error: storageError } = await supabase.storage
        .from('public-files')
        .remove([file.storage_path]);

      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from('general_files')
        .delete()
        .eq('id', file.id);

      if (dbError) throw dbError;

      await fetchGeneralFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Failed to delete file');
    }
  };

  const filteredPatientFiles = patientFiles.filter(file =>
    file.file_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    file.patients?.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    file.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredGeneralFiles = generalFiles.filter(file =>
    file.file_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    file.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const currentFiles = activeTab === 'patient' ? filteredPatientFiles : filteredGeneralFiles;

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      xray: 'bg-purple-100 text-purple-800',
      photo: 'bg-blue-100 text-blue-800',
      document: 'bg-gray-100 text-gray-800',
      report: 'bg-green-100 text-green-800',
      consent_form: 'bg-yellow-100 text-yellow-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getFileIcon = (fileType: string) => {
    if (fileType?.startsWith('image/')) {
      return <Image size={20} className="text-blue-600" />;
    }
    return <FileText size={20} className="text-gray-600" />;
  };

  return (
    <div>
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <FolderOpen className="text-blue-600" />
              Files
            </h2>
            <p className="text-gray-600 mt-1">Manage patient documents and images</p>
          </div>
          <button
            onClick={() => {
              setShowUploadModal(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Upload size={20} />
            Upload {activeTab === 'patient' ? 'Patient' : 'General'} Files
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4 flex gap-2">
          <button
            onClick={() => setActiveTab('patient')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'patient'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Patient Files
          </button>
          <button
            onClick={() => setActiveTab('general')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'general'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            General Files
          </button>
        </div>

        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentFiles.map((file) => (
              <div key={file.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    {getFileIcon(file.file_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">{file.file_name}</h3>
                    {activeTab === 'patient' && 'patients' in file && (
                      <p className="text-sm text-gray-600 mt-1">{file.patients?.full_name}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(file.category)}`}>
                        {file.category.replace('_', ' ')}
                      </span>
                      <span className="text-xs text-gray-500">{formatFileSize(file.file_size)}</span>
                    </div>
                    {file.description && (
                      <p className="text-sm text-gray-500 mt-2 line-clamp-2">{file.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-3">
                      {activeTab === 'general' && (
                        <button
                          onClick={() => handleCopyUrl(file as GeneralFile)}
                          className="text-green-600 hover:text-green-800 text-sm relative"
                          title="Copy Public URL"
                        >
                          {copiedUrl === file.id ? <Copy size={16} className="text-green-700" /> : <Link2 size={16} />}
                        </button>
                      )}
                      <button
                        onClick={() => handleDownload(file)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                        title="Download"
                      >
                        <Download size={16} />
                      </button>
                      <button
                        onClick={() => activeTab === 'patient' && 'patient_id' in file ? handleDeletePatient(file as PatientFile) : handleDeleteGeneral(file as GeneralFile)}
                        className="text-red-600 hover:text-red-800 text-sm"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    {activeTab === 'general' && copiedUrl === file.id && (
                      <p className="text-xs text-green-600 mt-1">URL copied!</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Upload Files</h3>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFiles([]);
                    setError('');
                    setUploadProgress({});
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <span className="text-red-800 text-sm">{error}</span>
                </div>
              )}

              <div className="space-y-4">
                {activeTab === 'patient' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Patient *
                    </label>
                    <select
                      value={selectedPatient}
                      onChange={(e) => setSelectedPatient(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={uploading}
                    >
                      <option value="">Select a patient</option>
                      {patients.map((patient) => (
                        <option key={patient.id} value={patient.id}>
                          {patient.full_name} ({patient.email})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={uploading}
                  >
                    <option value="document">Document</option>
                    <option value="xray">X-Ray</option>
                    <option value="photo">Photo</option>
                    <option value="report">Report</option>
                    <option value="consent_form">Consent Form</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    placeholder="Optional description for the file(s)"
                    disabled={uploading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Files * (Single or Multiple)
                  </label>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={uploading}
                  />
                  {selectedFiles.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <p className="text-sm text-gray-600 font-medium">
                        {selectedFiles.length} file(s) selected:
                      </p>
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="text-sm text-gray-600 flex items-center justify-between">
                          <span className="truncate">
                            {file.name} ({(file.size / 1024).toFixed(2)} KB)
                          </span>
                          {uploadProgress[file.name] !== undefined && (
                            <span className="ml-2 text-blue-600">
                              {uploadProgress[file.name]}%
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleUpload}
                  disabled={uploading || selectedFiles.length === 0 || (activeTab === 'patient' && !selectedPatient)}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Uploading...' : `Upload ${selectedFiles.length > 0 ? `${selectedFiles.length} File(s)` : 'Files'}`}
                </button>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFiles([]);
                    setError('');
                    setUploadProgress({});
                  }}
                  disabled={uploading}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Files;
